import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTelegram } from '../hooks/useTelegram';
import { usePlatform } from '../hooks/usePlatform';
import { QRCodeSheet } from './QRCodeSheet';
import { AppDownloadSheet } from './AppDownloadSheet';
import { ResetConfirmationSheet } from './ResetConfirmationSheet';
import { Notification } from "./Notification";
import { UpgradeConfirmationSheet } from './UpgradeConfirmationSheet';
import { LoadingSheet } from './LoadingSheet';
import { hapticFeedback, backButton } from '../utils/telegramSdk';
import './ResetConfirmationSheet.css';
import {ResetTrafficConfirmationSheet} from "./ResetTrafficConfirmationSheet";
import {InsufficientBalanceSheet} from "./InsufficientBalanceSheet";
import { AppSelectionSheet } from './AppSelectionSheet';
import { AppInstallSheet } from './AppInstallSheet';

const ASSET_PATH = '/images';
const API_URL = window.location.origin;

const copyTextToClipboard = async (text) => {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
        try {
            await navigator.clipboard.writeText(text);
            return;
        } catch {
            // Telegram WebView may expose Clipboard API but reject writeText.
        }
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    let copied = false;
    try {
        copied = document.execCommand('copy');
    } finally {
        textarea.remove();
    }
    if (!copied) throw new Error('Clipboard copy was rejected');
};


// Тарифы по количеству устройств
const DEVICE_TARIFFS = {
    1: 149, 2: 199, 3: 249, 4: 299, 5: 349,
    6: 399, 7: 449, 8: 499, 9: 549, 10: 599
};

// Периоды
const PERIODS = [1, 3, 6, 12];

// Константа для минут в месяце
const MINUTES_IN_MONTH = 43200; // 30 дней * 24 часа * 60 минут

// Функция для получения цены тарифа
const getTariffPrice = (devices, periodMonths) => {
    const basePrice = DEVICE_TARIFFS[devices] || DEVICE_TARIFFS[1];
    const multiplier = {
        1: 1,
        3: 2.7,
        6: 5.4,
        12: 10.8
    }[periodMonths] || 1;
    return Math.round(basePrice * multiplier);
};

// Функция для форматирования времени до сброса
const formatTimeUntilReset = (nextResetDate) => {
    if (!nextResetDate) return 'Не установлено';

    const now = new Date();
    const resetDate = new Date(nextResetDate);
    const diffMs = resetDate - now;

    if (diffMs <= 0) return 'Скоро';

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    const parts = [];
    if (days > 0) parts.push(`${days} ${declension(days, 'день', 'дня', 'дней')}`);
    if (hours > 0) parts.push(`${hours} ${declension(hours, 'час', 'часа', 'часов')}`);
    if (minutes > 0 && days === 0) parts.push(`${minutes} ${declension(minutes, 'минута', 'минуты', 'минут')}`);

    return parts.length > 0 ? parts.join(' ') : 'Менее минуты';
};

const formatTimeUntilResetWithOffset = (nextResetDate) => {
    if (!nextResetDate) return 'Не установлено';

    const now = new Date();
    const resetDate = new Date(nextResetDate);
    // Добавляем 3 часа к дате сброса для отображения в шторке
    const adjustedResetDate = new Date(resetDate.getTime() + (3 * 60 * 60 * 1000));
    const diffMs = adjustedResetDate - now;

    if (diffMs <= 0) return 'Скоро';

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    const parts = [];
    if (days > 0) parts.push(`${days} ${declension(days, 'день', 'дня', 'дней')}`);
    if (hours > 0) parts.push(`${hours} ${declension(hours, 'час', 'часа', 'часов')}`);
    if (minutes > 0 && days === 0) parts.push(`${minutes} ${declension(minutes, 'минута', 'минуты', 'минут')}`);

    return parts.length > 0 ? parts.join(' ') : 'Менее минуты';
};


const declension = (number, one, two, five) => {
    const n = Math.abs(number) % 100;
    const n1 = n % 10;
    if (n > 10 && n < 20) return five;
    if (n1 > 1 && n1 < 5) return two;
    if (n1 === 1) return one;
    return five;
};


export function KeyDetailPage({onBack, keyData, onGoWallet, closemenu, openmenu, tab, userId, onDataChanged}) {
    // Основное состояние ключа
    const [currentKey, setCurrentKey] = useState(keyData);
    const [devicesList, setDevicesList] = useState([]);
    const [isLoadingDevices, setIsLoadingDevices] = useState(false);
    const [deletingDeviceHwid, setDeletingDeviceHwid] = useState(null);
    const [nextTrafficReset, setNextTrafficReset] = useState(null);

    const[requiredAmount, setRequiredAmount] = useState(0);

    const [activeTab, setActiveTab] = useState(tab || 'management');
    const currentDevicesCount = currentKey?.devices || 1;
    const currentPeriodMonths = currentKey?.period_months || 1;

    const [devices, setDevices] = useState(currentDevicesCount);
    const [animatedDevices, setAnimatedDevices] = useState(currentDevicesCount);
    const [isAnimating, setIsAnimating] = useState(false);
    const [animationDirection, setAnimationDirection] = useState(null);

    // ИСПРАВЛЕНО: Получаем платформу пользователя и isTelegram из usePlatform
    const { platform: userPlatform } = usePlatform();

    // Инициализация selectedPlatform с определением платформы пользователя
    const [selectedPlatform, setSelectedPlatform] = useState(userPlatform);

    const [showQRCode, setShowQRCode] = useState(false);
    const [showAppSheet, setShowAppSheet] = useState(false);
    const [showAppInstallSheet, setShowAppInstallSheet] = useState(false);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
    const [selectedApp, setSelectedApp] = useState(null);
    const [showUpgradeConfirm, setShowUpgradeConfirm] = useState(false);
    const [upgradeDetails, setUpgradeDetails] = useState(null);
    const [showLoadingSheet, setShowLoadingSheet] = useState(false);
    const [notification, setNotification] = useState({isVisible: false, message: '', type: 'success'});
    const [isResetting, setIsResetting] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [isChangingTariff, setIsChangingTariff] = useState(false);
    const [currentBalance, setCurrentBalance] = useState(null);
    const [showInsufficientBalanceSheet, setShowInsufficientBalanceSheet] = useState(false);
    const [showAppSelectionSheet, setShowAppSelectionSheet] = useState(false);

    // Слайдер
    const [isDragging, setIsDragging] = useState(false);
    const [smoothDevices, setSmoothDevices] = useState(currentDevicesCount);
    const sliderRef = useRef(null);
    const thumbRef = useRef(null);
    const fillRef = useRef(null);
    const animationRef = useRef(null);
    const animationTimeoutRef = useRef(null);

    // ИСПРАВЛЕНО: Переименовываем isTelegram в isTelegramApp
    const { isTelegram: isTelegramApp } = useTelegram();

    // Refs для табов
    const tabContainerRef = useRef(null);
    const tabRefs = {
        management: useRef(null),
        change: useRef(null),
        devices: useRef(null)
    };
    const [tabHighlightStyle, setTabHighlightStyle] = useState({left: 0, width: 0});

    const platformContainerRef = useRef(null);
    const platformRefs = {android: useRef(null), ios: useRef(null), windows: useRef(null), macos: useRef(null)};
    const [platformHighlightStyle, setPlatformHighlightStyle] = useState({left: 0, width: 0});
    let TRACK_END;
    const MIN_DEVICES = currentDevicesCount;
    const MAX_DEVICES = 10;
    const TRACK_START = 0;

    // ИСПРАВЛЕНО: userPlatform теперь строка
    if (userPlatform === 'android') {
        TRACK_END = 270;
    } else if (userPlatform === 'ios') {
        TRACK_END = 290;
    } else if (userPlatform === 'windows') {
        TRACK_END = 300;
    } else {
        TRACK_END = 310;
    }

    const SLIDER_WIDTH = TRACK_END - TRACK_START;

    // Переход в кошелек для пополнения
    const handleGoToWallet = () => {
        setShowInsufficientBalanceSheet(false);
        if (onGoWallet) {
            onGoWallet();
        }
    };

    const handleConnectVPN = () => {
        setShowAppSelectionSheet(true);
    };

    const handleAppSelect = (app) => {
        // Закрываем шторку выбора
        setShowAppSelectionSheet(false);
        // Открываем шторку с деталями приложения
        setSelectedApp({...app, keyUrl: keyInfo.url});
        setShowAppInstallSheet(true);
        closemenu();
    };

    // Получение баланса пользователя
    const fetchBalance = useCallback(async () => {
        if (!userId) return 0;
        try {
            const response = await fetch(`${API_URL}/api/user/${userId}/balance`);
            const data = await response.json();
            return data.balance || 0;
        } catch (error) {
            console.error('Ошибка получения баланса:', error);
            return 0;
        }
    }, [userId]);

    // ИСПРАВЛЕНО: Обновляем selectedPlatform только когда userPlatform действительно меняется
    useEffect(() => {
        if (userPlatform && userPlatform !== selectedPlatform) {
            setSelectedPlatform(userPlatform);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userPlatform]);

    // Синхронизация keyData
    useEffect(() => {
        if (keyData) setCurrentKey(keyData);
        if (tab) {
            setActiveTab('change');
            setTimeout(() => {
                if (tabContainerRef.current && tabRefs.change?.current) {
                    const containerRect = tabContainerRef.current.getBoundingClientRect();
                    const activeRect = tabRefs.change.current.getBoundingClientRect();
                    setTabHighlightStyle({
                        left: activeRect.left - containerRect.left,
                        width: activeRect.width
                    });
                }
            }, 100);
        }
    }, [keyData, tab]);

    // Загрузка списка устройств при открытии вкладки
    useEffect(() => {
        if (activeTab === 'devices' && currentKey?.uuid) {
            fetchDevicesList();
        }
    }, [activeTab, currentKey]);

    // Обновление информации о сбросе трафика
    useEffect(() => {
        if (currentKey?.next_reset_at) {
            setNextTrafficReset(currentKey.next_reset_at);
        }
    }, [currentKey]);

    // Данные ключа
    const keyInfo = {
        id: currentKey?.id,
        uuid: currentKey?.uuid,
        key_number: currentKey?.key_number || currentKey?.uuid?.slice(0, 8).toUpperCase() || 'HYDR-...',
        fullId: currentKey?.key_number || currentKey?.uuid || '#HYDR-...',
        protocol: currentKey?.protocol || 'VLESS',
        status: currentKey?.status || 'expired',
        url: currentKey?.subscription_url || '',
        showUrl: Boolean(currentKey?.subscription_url),
        expireAt: currentKey?.expire_at,
        expiryText: currentKey?.expire_at ? (new Date(currentKey.expire_at) > new Date() ? 'Активен' : 'Закончился') : 'Закончился',
        devices: currentKey?.devices,
        periodMonths: currentKey?.period_months || 1,
        nextResetAt: currentKey?.next_reset_at
    };
    const timeUntilResetWithOffset = formatTimeUntilResetWithOffset(keyInfo.nextResetAt);



    const diffMs = keyInfo.expireAt - Date.now(); // Разница в миллисекундах
    const diffMsReset = new Date(keyInfo.nextResetAt) - Date.now();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffDaysReset = Math.floor(diffMsReset / (1000 * 60 * 60 * 24));
    let RESET_PRICE;
    if (diffDaysReset > 0) {
        RESET_PRICE = (diffDaysReset > 5 ? 99 : 50)  ;
    } else
    {
        RESET_PRICE = 50;
    }

    // Массив приложений с указанием поддерживаемых платформ
    const apps = [
        {
            id: 1,
            name: 'INCY',
            icon: 'incy.png',
            descriptionIos: 'Это клиент для iOS, предназначенный для подключения к VPN-сервисам. Он гарантирует безопасность и анонимность интернет-соединения.',
            descriptionMacos: 'Это клиент для MacOS, предназначенный для подключения к VPN-сервисам. Он гарантирует безопасность и анонимность интернет-соединения.',
            descriptionWindows: 'Это клиент для Windows, предназначенный для подключения к VPN-сервисам. Он гарантирует безопасность и анонимность интернет-соединения.',
            descriptionAndroid: 'Это клиент для Android, предназначенный для подключения к VPN-сервисам. Он гарантирует безопасность и анонимность интернет-соединения.',
            platforms: ['android', 'ios', 'macos'],
            deepLink: 'incy://add/{link}',
            androidGoogleUrl: 'https://play.google.com/store/apps/details?id=llc.itdev.incy&hl=ru',
            androidApkUrl: 'https://github.com/INCY-DEV/incy-platforms/releases/latest/download/Incy.apk',
            macosUrl: 'https://apps.apple.com/ru/app/incy/id6756943388',
            windowsUrl: 'https://apps.apple.com/ru/app/incy/id6756943388',
            iosRuUrl: 'https://apps.apple.com/ru/app/incy/id6756943388',
            iosGlobalUrl: 'https://apps.apple.com/us/app/incy/id6756943388'
        },
        {
            id: 2,
            name: 'Happ',
            icon: 'happ.png',
            descriptionIos: 'Это клиент для iOS, предназначенный для подключения к VPN-сервисам. Он гарантирует безопасность и анонимность интернет-соединения.',
            descriptionMacos: 'Это клиент для MacOS, предназначенный для подключения к VPN-сервисам. Он гарантирует безопасность и анонимность интернет-соединения.',
            descriptionWindows: 'Это клиент для Windows, предназначенный для подключения к VPN-сервисам. Он гарантирует безопасность и анонимность интернет-соединения.',
            descriptionAndroid: 'Это клиент для Android, предназначенный для подключения к VPN-сервисам. Он гарантирует безопасность и анонимность интернет-соединения.',
            platforms: ['android', 'ios', 'windows', 'macos'],
            deepLink: 'happ://add/{link}',
            androidGoogleUrl: 'https://play.google.com/store/apps/details?id=com.happproxy',
            androidApkUrl: 'https://github.com/Happ-proxy/happ-android/releases/latest/download/Happ.apk',
            windowsUrl: 'https://github.com/Happ-proxy/happ-desktop/releases/latest/download/setup-Happ.x64.exe',
            iosRuUrl: 'https://apps.apple.com/ru/app/happ-proxy-utility-plus/id6746188973',
            iosGlobalUrl: 'https://apps.apple.com/us/app/happ-proxy-utility/id6504287215'
        }
    ];

    // Фильтруем приложения для текущей платформы
    const filteredApps = useMemo(() => {
        return apps.filter(app => app.platforms.includes(selectedPlatform));
    }, [selectedPlatform]);

    // Если для платформы нет приложений, показываем все
    const displayApps = filteredApps.length > 0 ? filteredApps : apps;

    // ==================== ФУНКЦИИ ДЛЯ УПРАВЛЕНИЯ УСТРОЙСТВАМИ ====================

    // Получение списка устройств
    const fetchDevicesList = async () => {
        if (!currentKey?.uuid) return;

        setIsLoadingDevices(true);
        try {
            const response = await fetch(`${API_URL}/api/key/${currentKey.uuid}/devices`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (data.success) {
                setDevicesList(data.devices || []);
            } else {
                showNotification('Ошибка загрузки устройств', 'error');
                setDevicesList([]);
            }
        } catch (error) {
            console.error('Ошибка загрузки устройств:', error);
            showNotification('Ошибка загрузки списка устройств', 'error');
            setDevicesList([]);
        } finally {
            setIsLoadingDevices(false);
        }
    };

    // Удаление устройства
    const handleDeleteDevice = async (hwid) => {
        if (!currentKey?.uuid) return;

        setDeletingDeviceHwid(hwid);

        try {
            const response = await fetch(`${API_URL}/api/key/${currentKey.uuid}/devices/${hwid}`, {
                method: 'DELETE',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({userId: userId})
            });

            const data = await response.json();

            if (data.success) {
                showNotification('Устройство успешно отвязано!', 'success');
                await fetchDevicesList();
            } else {
                showNotification('Ошибка при отвязке устройства', 'error');
            }
        } catch (error) {
            console.error('Ошибка удаления устройства:', error);
            showNotification('Ошибка при отвязке устройства', 'error');
        } finally {
            setDeletingDeviceHwid(null);
        }
    };

    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        return date.toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Получение названия платформы
    const getPlatformName = (platform) => {
        if (!platform) return 'Неизвестное устройство';
        const platforms = {
            'windows': 'Windows',
            'macos': 'macOS',
            'linux': 'Linux',
            'android': 'Android',
            'ios': 'iOS',
            'web': 'Web'
        };
        return platforms[platform.toLowerCase()] || platform;
    };

    // ==================== ФУНКЦИИ РАСЧЕТА ВРЕМЕНИ ====================
    const getRemainingTime = useCallback(() => {
        if (!keyInfo.expireAt) return {days: 0, hours: 0, minutes: 0, totalMinutes: 0};
        const diffMs = new Date(keyInfo.expireAt).setHours(new Date(keyInfo.expireAt).getHours() + 3) - new Date();

        if (diffMs <= 0) return {days: 0, hours: 0, minutes: 0, totalMinutes: 0};
        const totalMinutes = Math.floor(diffMs / (1000 * 60));
        const days = Math.floor(totalMinutes / (24 * 60));
        const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
        const minutes = totalMinutes % 60;
        return {days, hours, minutes, totalMinutes};
    }, [keyInfo.expireAt]);

    const formatTimeRemaining = (days, hours, minutes) => `${days} дн ${hours} ч ${minutes} мин`;

    // Вычисляем остаточное время
    const remainingTime = getRemainingTime();
    const currentTimeRemaining = formatTimeRemaining(remainingTime.days, remainingTime.hours, remainingTime.minutes);
    const timeUntilReset = formatTimeUntilReset(keyInfo.nextResetAt);

    // Цены для отображения
    const currentPrice = getTariffPrice(currentDevicesCount, currentPeriodMonths);
    const newPrice = getTariffPrice(devices, currentPeriodMonths);
    const priceDiff = newPrice - currentPrice;

    // НОВАЯ ФУНКЦИЯ РАСЧЕТА НОВОГО СРОКА
    const calculateNewTimeRemaining = useCallback(() => {
        if (devices <= currentDevicesCount) return currentTimeRemaining;

        const remainingMinutes = remainingTime.totalMinutes;
        if (remainingMinutes <= 0) return '0 дн 0 ч 0 мин';

        // Рассчитываем оставшуюся стоимость в рублях
        const currentPricePerMinute = currentPrice / MINUTES_IN_MONTH;
        const remainingValue = remainingMinutes * currentPricePerMinute;

        // Рассчитываем новое время в минутах
        const newPricePerMinute = newPrice / MINUTES_IN_MONTH;
        let newMinutes = Math.floor(remainingValue / newPricePerMinute);
        newMinutes = Math.max(1, newMinutes);

        // Форматируем
        const newDays = Math.floor(newMinutes / (24 * 60));
        const newHours = Math.floor((newMinutes % (24 * 60)) / 60);
        const newMins = newMinutes % 60;

        return `${newDays} дн ${newHours} ч ${newMins} мин`;
    }, [devices, currentDevicesCount, currentPrice, newPrice, remainingTime.totalMinutes, currentTimeRemaining]);

    const newTimeRemaining = calculateNewTimeRemaining();
    const addedDevices = devices - currentDevicesCount;
    const canUpgrade = devices > currentDevicesCount && devices <= MAX_DEVICES;
    const displayDevices = Math.round(smoothDevices);

    // ==================== СЛАЙДЕР ФУНКЦИИ ====================
    const triggerDeviceAnimation = useCallback((newValue) => {
        const direction = newValue > animatedDevices ? 'increase' : 'decrease';
        if (hapticFeedback.impactOccurred?.isAvailable()) hapticFeedback.impactOccurred('light');
        if (newValue !== animatedDevices) {
            setAnimationDirection(direction);
            setIsAnimating(true);
            setAnimatedDevices(newValue);
            if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
            animationTimeoutRef.current = setTimeout(() => {
                setIsAnimating(false);
                setAnimationDirection(null);
            }, 250);
        }
    }, [animatedDevices]);

    const updateSliderPosition = useCallback((value) => {
        if (!thumbRef.current || !sliderRef.current) return;
        const percent = (value - MIN_DEVICES) / (MAX_DEVICES - MIN_DEVICES);
        const center = TRACK_START + percent * SLIDER_WIDTH;
        thumbRef.current.style.left = `${center}px`;
        if (fillRef.current) {
            const fillWidth = Math.max(0, center - TRACK_START);
            fillRef.current.style.width = `${fillWidth}px`;
        }
    }, [MIN_DEVICES, MAX_DEVICES, TRACK_START, SLIDER_WIDTH]);

    const getValueFromPosition = useCallback((clientX, rect) => {
        let x = clientX - rect.left;
        x = Math.max(TRACK_START, Math.min(x, TRACK_END));
        const percent = (x - TRACK_START) / SLIDER_WIDTH;
        let newValue = MIN_DEVICES + percent * (MAX_DEVICES - MIN_DEVICES);
        return Math.max(MIN_DEVICES, Math.min(MAX_DEVICES, newValue));
    }, [MIN_DEVICES, MAX_DEVICES, TRACK_START, TRACK_END, SLIDER_WIDTH]);

    const animateToValue = useCallback((targetValue, duration = 200) => {
        if (animationRef.current) cancelAnimationFrame(animationRef.current);
        const startValue = smoothDevices;
        const startTime = performance.now();
        const distance = targetValue - startValue;
        if (Math.abs(distance) < 0.01) {
            setSmoothDevices(targetValue);
            updateSliderPosition(targetValue);
            return;
        }
        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(1, elapsed / duration);
            const easeProgress = 1 - Math.pow(1 - progress, 3);
            const currentValue = startValue + distance * easeProgress;
            setSmoothDevices(currentValue);
            updateSliderPosition(currentValue);
            const roundedValue = Math.round(currentValue);
            if (roundedValue !== animatedDevices && roundedValue >= MIN_DEVICES) triggerDeviceAnimation(roundedValue);
            if (progress < 1) animationRef.current = requestAnimationFrame(animate);
            else {
                setSmoothDevices(targetValue);
                updateSliderPosition(targetValue);
                animationRef.current = null;
            }
        };
        animationRef.current = requestAnimationFrame(animate);
    }, [smoothDevices, updateSliderPosition, triggerDeviceAnimation, animatedDevices, MIN_DEVICES]);

    const handleDragStart = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        if (thumbRef.current) thumbRef.current.classList.add('dragging');
    }, []);

    const addSheetOpenClass = () => {
        document.body.classList.add('sheet-open');
    };

    const removeSheetOpenClass = () => {
        document.body.classList.remove('sheet-open');
    };

    const handleDragMove = useCallback((e) => {
        if (!isDragging || !sliderRef.current) return;
        e.preventDefault();
        const clientX = e.clientX ?? e.touches?.[0]?.clientX;
        if (!clientX) return;
        const rect = sliderRef.current.getBoundingClientRect();
        const rawValue = getValueFromPosition(clientX, rect);
        const roundedValue = Math.round(rawValue);
        setSmoothDevices(rawValue);
        updateSliderPosition(rawValue);
        if (roundedValue !== devices && roundedValue >= MIN_DEVICES) {
            triggerDeviceAnimation(roundedValue);
            setDevices(roundedValue);
        }
    }, [isDragging, devices, MIN_DEVICES, getValueFromPosition, updateSliderPosition, triggerDeviceAnimation]);

    const handleDragEnd = useCallback(() => {
        setIsDragging(false);
        if (thumbRef.current) thumbRef.current.classList.remove('dragging');
        const roundedValue = Math.round(smoothDevices);
        const finalValue = Math.max(MIN_DEVICES, Math.min(MAX_DEVICES, roundedValue));
        if (finalValue !== devices) {
            animateToValue(finalValue, 150);
            setDevices(finalValue);
        } else {
            animateToValue(finalValue, 150);
        }
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
        document.removeEventListener('touchmove', handleDragMove);
        document.removeEventListener('touchend', handleDragEnd);
    }, [isDragging, smoothDevices, devices, MIN_DEVICES, MAX_DEVICES, animateToValue, handleDragMove]);

    const handleTrackClick = useCallback((e) => {
        if (e.target === thumbRef.current || thumbRef.current?.contains(e.target)) return;
        if (!sliderRef.current) return;
        const rect = sliderRef.current.getBoundingClientRect();
        const clientX = e.clientX ?? e.touches?.[0]?.clientX;
        if (!clientX) return;
        const rawValue = getValueFromPosition(clientX, rect);
        const roundedValue = Math.round(rawValue);
        if (roundedValue !== devices && roundedValue >= MIN_DEVICES) {
            setDevices(roundedValue);
            triggerDeviceAnimation(roundedValue);
            animateToValue(roundedValue, 200);
        }
    }, [devices, MIN_DEVICES, getValueFromPosition, animateToValue, triggerDeviceAnimation]);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleDragMove);
            document.addEventListener('mouseup', handleDragEnd);
            document.addEventListener('touchmove', handleDragMove, {passive: false});
            document.addEventListener('touchend', handleDragEnd);
            return () => {
                document.removeEventListener('mousemove', handleDragMove);
                document.removeEventListener('mouseup', handleDragEnd);
                document.removeEventListener('touchmove', handleDragMove);
                document.removeEventListener('touchend', handleDragEnd);
            };
        }
    }, [isDragging, handleDragMove, handleDragEnd]);

    useEffect(() => {
        if (sliderRef.current && thumbRef.current) {
            updateSliderPosition(devices);
            setSmoothDevices(devices);
            setAnimatedDevices(devices);
        }
    }, [devices, updateSliderPosition]);

    // ИСПРАВЛЕНО: Убрали повторный вызов useTelegram, используем isTelegramApp

    // ==================== ХАЙЛАЙТ ====================
    // ИСПРАВЛЕНО: Используем isTelegramApp вместо isTelegram
    useEffect(() => {
        if (isTelegramApp && backButton) {
            try {
                backButton.mount();
                backButton.show();
                backButton.onClick(() => {
                    onBack();
                    backButton.hide();
                });
            } catch (error) {
                console.error('Ошибка инициализации backButton:', error);
            }
        }

        return () => {
            if (isTelegramApp && backButton) {
                try {
                    backButton.hide();
                } catch (error) {
                    // игнорируем
                }
            }
        };
    }, [onBack, isTelegramApp]);

    useEffect(() => {
        if (tabContainerRef.current && tabRefs[activeTab]?.current) {
            const containerRect = tabContainerRef.current.getBoundingClientRect();
            const activeRect = tabRefs[activeTab].current.getBoundingClientRect();
            setTabHighlightStyle({left: activeRect.left - containerRect.left, width: activeRect.width});
        }
    }, [activeTab]);

    // ИСПРАВЛЕНО: Обновление хайлайта для платформ с проверкой
    useEffect(() => {
        if (platformContainerRef.current && platformRefs[selectedPlatform]?.current) {
            const containerRect = platformContainerRef.current.getBoundingClientRect();
            const activeRect = platformRefs[selectedPlatform].current.getBoundingClientRect();
            const newLeft = activeRect.left - containerRect.left;
            const newWidth = activeRect.width;

            setPlatformHighlightStyle(prev => {
                if (prev.left !== newLeft || prev.width !== newWidth) {
                    return {left: newLeft, width: newWidth};
                }
                return prev;
            });
        }
    }, [selectedPlatform]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (tabContainerRef.current && tabRefs.management?.current) {
                const containerRect = tabContainerRef.current.getBoundingClientRect();
                const activeRect = tabRefs.management.current.getBoundingClientRect();
                setTabHighlightStyle({left: activeRect.left - containerRect.left, width: activeRect.width});
            }
            if (platformContainerRef.current && platformRefs.android?.current) {
                const containerRect = platformContainerRef.current.getBoundingClientRect();
                const activeRect = platformRefs.android.current.getBoundingClientRect();
                // Добавляем проверку перед установкой
                const newLeft = activeRect.left - containerRect.left;
                const newWidth = activeRect.width;
                setPlatformHighlightStyle(prev => {
                    if (prev.left !== newLeft || prev.width !== newWidth) {
                        return {left: newLeft, width: newWidth};
                    }
                    return prev;
                });
            }
        }, 0);
        return () => clearTimeout(timeout);
    }, []);

    // ==================== ОСТАЛЬНЫЕ ФУНКЦИИ ====================
    const platforms = [
        {id: 'android', label: 'Android'},
        {id: 'ios', label: 'iOS'},
        {id: 'windows', label: 'Windows'},
        {id: 'macos', label: 'macOS'}
    ];

    const showNotification = (message, type = 'success') => {
        setNotification({isVisible: true, message, type});
        setTimeout(() => setNotification({isVisible: false, message: '', type: 'success'}), 3000);
    };

    const handleCopy = async () => {
        if (!keyInfo.url) return;
        try {
            await copyTextToClipboard(keyInfo.url);
            showNotification('Ссылка скопирована!', 'success');
            if (hapticFeedback.impactOccurred?.isAvailable()) {
                hapticFeedback.impactOccurred('heavy');
                setTimeout(() => hapticFeedback.impactOccurred('heavy'), 350);
            }
        } catch {
            showNotification('Ошибка при копировании', 'error');
        }
    };

    const handleScan = () => setShowQRCode(true);
    const handleCloseQR = () => setShowQRCode(false);
    const handleAppClick = (app) => {
        if (!keyInfo.showUrl) {
            showNotification('Для этого ключа используйте код подключения', 'success');
            return;
        }
        setSelectedApp({...app, keyUrl: keyInfo.url});
        setShowAppSheet(true);
    };
    const handleCloseAppSheet = () => {
        setShowAppSheet(false);
        setSelectedApp(null);
    };

    const handleCloseAppInstallSheet = () => {
        setShowAppInstallSheet(false);
        openmenu();
        setSelectedApp(null);
    };

    // Функция для расчета новой даты истечения
    const calculateNewExpireDate = useCallback(() => {
        const remainingMinutes = remainingTime.totalMinutes;
        const currentPrice = getTariffPrice(currentDevicesCount, currentPeriodMonths);
        const newPrice = getTariffPrice(devices, currentPeriodMonths);

        const currentPricePerMinute = currentPrice / MINUTES_IN_MONTH;
        const remainingValue = remainingMinutes * currentPricePerMinute;

        const newPricePerMinute = newPrice / MINUTES_IN_MONTH;
        let newMinutes = Math.floor(remainingValue / newPricePerMinute);
        newMinutes = Math.max(1, newMinutes);

        const now = new Date();
        const newExpireDate = new Date(now.getTime() + newMinutes * 60 * 1000);
        return newExpireDate.toISOString();
    }, [remainingTime.totalMinutes, currentDevicesCount, currentPeriodMonths, devices]);

    // Обработчик смены тарифа
    const handleChangeTariff = () => {
        if (devices === currentDevicesCount) {
            showNotification('Количество устройств не изменилось', 'warning');
            return;
        }
        if (devices < currentDevicesCount) {
            showNotification('Нельзя уменьшить количество устройств', 'error');
            return;
        }

        setUpgradeDetails({
            currentDevices: currentDevicesCount,
            newDevices: devices,
            currentTimeRemaining,
            newTimeRemaining,
            addedDevices: devices - currentDevicesCount,
            currentPrice,
            newPrice,
            priceDiff,
            currentPeriodMonths
        });
        setShowUpgradeConfirm(true);
    };

    // Подтверждение смены тарифа
    const handleUpgradeConfirm = async () => {
        setIsChangingTariff(true);
        setShowLoadingSheet(true);

        try {
            const response = await fetch(`${API_URL}/api/key/${currentKey.uuid}/change-devices-time`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    userId: userId,
                    newDevices: devices
                })
            });

            const data = await response.json();

            if (data.success) {
                showNotification(`Тариф успешно изменен! Новый срок: ${newTimeRemaining}`, 'success');
                const updatedKey = onDataChanged
                    ? await onDataChanged(currentKey.uuid)
                    : await refreshKeyData();
                if (updatedKey) setCurrentKey(updatedKey);
                setActiveTab('management');
            } else {
                showNotification('Ошибка при смене тарифа', 'error');
            }
        } catch (error) {
            console.error('Ошибка при смене тарифа:', error);
            showNotification('Ошибка при смене тарифа', 'error');
        } finally {
            setIsChangingTariff(false);
            setShowLoadingSheet(false);
            setShowUpgradeConfirm(false);
        }
    };

    const handleUpgradeCancel = () => {
        setShowUpgradeConfirm(false);
        setUpgradeDetails(null);
    };

    const handleResetClick = () => {
        if (!keyInfo.id) return showNotification('ID ключа не найден', 'error');
        setShowResetConfirm(true);
    };

    const handleResetConfirm = async () => {
        setShowResetConfirm(false);
        setIsResetting(true);
        setShowLoadingSheet(true);
        closemenu();

        try {
            if (!currentKey?.uuid) {
                showNotification('UUID ключа не найден', 'error');
                return;
            }

            console.log('🔄 Отправка запроса на сброс трафика для ключа:', currentKey.uuid);

            const response = await fetch(`${API_URL}/api/key/${currentKey.uuid}/reset`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({userId: userId})
            });

            const data = await response.json();

            if (data.success) {
                showNotification('Трафик успешно сброшен!', 'success');
                const updatedKey = onDataChanged
                    ? await onDataChanged(currentKey.uuid)
                    : await refreshKeyData();
                if (updatedKey) setCurrentKey(updatedKey);

                // Если вернулся новый баланс, обновляем его
                if (data.newBalance !== undefined) {
                    window.dispatchEvent(new CustomEvent('balance-updated', {detail: {balance: data.newBalance}}));
                }
            } else {
                if (data.error === 'insufficient_balance') {
                    const balance = await fetchBalance();
                    setCurrentBalance(balance);
                    setRequiredAmount(RESET_PRICE - balance);
                    setShowInsufficientBalanceSheet(true);

                } else {
                    showNotification( 'Ошибка при сбросе трафика', 'error');
                }
            }
        } catch (error) {
            console.error('❌ Ошибка при сбросе трафика:', error);
            showNotification('❌ Ошибка соединения', 'error');
        } finally {
            openmenu();
            setIsResetting(false);
            setShowLoadingSheet(false);
        }
    };

    // Закрытие шторки недостаточного баланса
    const handleCloseInsufficientBalance = () => {
        setShowInsufficientBalanceSheet(false);
    };

    const handleRegenerateClick = () => {
        if (!keyInfo.id) return showNotification('ID ключа не найден', 'error');
        setShowRegenerateConfirm(true);
    };

    const handleRegenerateConfirm = async () => {
        setShowRegenerateConfirm(false);
        setIsRegenerating(true);

        try {
            if (!currentKey?.uuid) {
                showNotification('UUID ключа не найден', 'error');
                return;
            }

            const response = await fetch(`${API_URL}/api/key/${currentKey.uuid}/regenerate`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({userId})
            });

            const data = await response.json();

            if (data.success) {
                showNotification('Ключ успешно перевыпущен!', 'success');

                // ✅ Полностью перезагружаем ключ с сервера
                const updatedKey = onDataChanged
                    ? await onDataChanged(currentKey.uuid)
                    : await refreshKeyData();
                if (updatedKey) setCurrentKey(updatedKey);
            } else {
                showNotification('Ошибка',
                    data.error === 'rate_limited' ? 'warning' : 'error');
            }
        } catch (error) {
            console.error('Ошибка регенерации:', error);
            showNotification('Ошибка соединения', 'error');
        } finally {
            setIsRegenerating(false);
        }
    };

    // Добавьте эту функцию для перезагрузки данных ключа
    const refreshKeyData = async () => {
        if (!currentKey?.uuid) return null;

        try {
            const response = await fetch(`${API_URL}/api/key/${currentKey.uuid}`, { cache: 'no-store' });
            const data = await response.json();

            if (data.success && data.key) {
                setCurrentKey(prev => ({
                    ...prev,
                    ...data.key,
                    last_regenerated_at: new Date().toISOString()
                }));
                return data.key;
            }
        } catch (error) {
            console.error('Ошибка обновления данных ключа:', error);
        }
        return null;
    };

    return (
        <>
            {/* ИСПРАВЛЕНО: Используем isTelegramApp */}
            {!isTelegramApp && (
                <button
                    onClick={onBack}
                    className="web-back-button-left"
                >
                    ← Назад
                </button>
            )}
            <div className="key-detail-page page-enter-animate">
                <div className="key-detail-content">
                    {/* Табы с хайлайтом */}
                    <div className="tab-control" ref={tabContainerRef}>
                        <div className="tab-highlight"
                             style={{left: tabHighlightStyle.left, width: tabHighlightStyle.width}}/>
                        <div className={`tab-option ${activeTab === 'management' ? 'active' : ''}`} ref={tabRefs.management}
                             onClick={() => setActiveTab('management')}>
                            <span>Управление</span>
                        </div>
                        <div className={`tab-option ${activeTab === 'change' ? 'active' : ''}`} ref={tabRefs.change}
                             onClick={() => setActiveTab('change')}>
                            <span>Смена тарифа</span>
                        </div>
                        <div className={`tab-option ${activeTab === 'devices' ? 'active' : ''}`} ref={tabRefs.devices}
                             onClick={() => setActiveTab('devices')}>
                            <span>Устройства</span>
                        </div>
                    </div>

                    <div className="tab-content-container">
                        {/* ==================== ВКЛАДКА УПРАВЛЕНИЕ ==================== */}
                        <div className={`tab-content ${activeTab === 'management' ? 'active' : ''}`}>
                            <div className="status-card" style={{padding: '12px 0'}}>
                                <div className="card-content" style={{gap: '8px', padding: '0 12px'}}>
                                    <div className="status-row">
                                        <div className="status-badge">
                                        <span
                                            className="status-text">{keyInfo.expiryText === "Активен" ? 'Оплачен' : 'Не оплачен'}</span>
                                        </div>
                                        <span className="protocol-badge">{keyInfo.protocol}</span>
                                    </div>
                                    <div className="status-row">
                                        <span className="user-id">{keyInfo.fullId}</span>
                                    </div>
                                    <div className="status-row">
                                        <div className="expiry-row">
                                            {keyInfo.expiryText === "Активен" ? (
                                                <div className="expiry-content">
                                                    <img src="/images/clock-green.svg" className="clock-icon" alt="Clock"/>
                                                    <span className="norm-text">{keyInfo.expiryText}</span>
                                                </div>
                                            ) : (
                                                <div className="expiry-content">
                                                    <img src="/images/clock.svg" className="clock-icon" alt="Clock"/>
                                                    <span className="expiry-text">{keyInfo.expiryText}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="device-badge">
                                        <span
                                            className="devices-badge">{keyInfo.devices} {keyInfo.devices == 1 ? 'устройство' : 'устройств'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div style={{paddingTop: '15px', margin: '0 10px 0 10px' }}>
                                    {keyInfo.showUrl && (
                                        <div className="input-container" style={{padding: '12px 12px'}}>
                                            <span className="input-url">{keyInfo.url}</span>
                                            <div className="input-actions">
                                                <button className="icon-btn" onClick={handleCopy} aria-label="Скопировать ссылку">
                                                    <img src="/images/copy.svg" alt=""/>
                                                </button>
                                                <button className="icon-btn" onClick={handleScan} aria-label="Показать QR-код ссылки">
                                                    <img src="/images/qr.svg" alt=""/>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    <div style={{alignItems: 'center', justifyContent: 'center', display: 'flex'}}>
                                        <p className="subtitle" style={{paddingTop: '10px'}}>
                                            Скопируйте ссылку и добавьте её в приложение
                                        </p>
                                    </div>
                                </div>
                                <div style={{paddingTop: '15px', margin: '0 10px 0 10px' }}>
                                    <button
                                        className="btn btn-green"
                                        onClick={handleConnectVPN}
                                        disabled={!keyInfo.showUrl}
                                    >
                                        {isResetting ? 'Подключение...' : 'Подключить VPN'}
                                    </button>
                                </div>
                            </div>

                            <div className="action-buttons-container" style={{gap: '8px', margin: '8px 0 4px'}}>
                                <button
                                    className="reset-button"
                                    onClick={handleResetClick}
                                    disabled={isResetting || keyInfo.expiryText === 'Закончился'}
                                >
                                    {isResetting ? 'Сброс...' : 'Сбросить трафик'}
                                </button>
                                <button
                                    className="reset-button"
                                    onClick={handleRegenerateClick}
                                    disabled={isRegenerating || keyInfo.expiryText === 'Закончился'}
                                >
                                    {isRegenerating ? 'Обновление...' : 'Перевыпустить ключ'}
                                </button>
                            </div>

                            <div className="platform-selector" ref={platformContainerRef}>
                                <div className="platform-highlight"
                                     style={{left: platformHighlightStyle.left, width: platformHighlightStyle.width}}/>
                                {platforms.map(platform => (
                                    <div key={platform.id}
                                         className={`platform-item ${selectedPlatform === platform.id ? 'active' : ''}`}
                                         ref={platformRefs[platform.id]} onClick={() => setSelectedPlatform(platform.id)}>
                                        <span>{platform.label}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="app-grid" style={{gap: '6px'}}>
                                {displayApps.map(app => (
                                    <div key={app.id} className="app-item" onClick={() => handleAppClick(app)}
                                         style={{cursor: 'pointer', gap: '4px'}}>
                                        <img src={`${ASSET_PATH}/${app.icon}`} className="app-icon"
                                             style={{width: '56px', height: '56px'}} alt={app.name}/>
                                        <span className="app-name" style={{fontSize: '10px'}}>{app.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* ==================== ВКЛАДКА СМЕНА ТАРИФА ==================== */}
                        <div className={`tab-content ${activeTab === 'change' ? 'active' : ''}`}>
                            <div className="header-section">
                                <h1 className="main-title">Изменить тариф для {keyInfo.key_number}</h1>
                                <p className="subtitle">Увеличьте количество устройств</p>
                            </div>

                            <div className="card slider-card">
                                <div className="card-row price-row">
                                    <span className="label-text">Количество устройств</span>
                                    <span
                                        className={`value-text ${isAnimating ? 'device-count-animate' : ''} ${animationDirection === 'increase' ? 'animate-increase' : ''} ${animationDirection === 'decrease' ? 'animate-decrease' : ''}`}
                                        key={displayDevices}>
                                    {displayDevices}
                                </span>
                                </div>
                                <div className="slider-component" ref={sliderRef} onClick={handleTrackClick}
                                     onTouchStart={handleTrackClick}>
                                    <div className="slider-track"></div>
                                    <div className="slider-fill slider-fill-smooth" ref={fillRef}></div>
                                    <div className="slider-thumb slider-thumb-smooth" ref={thumbRef}
                                         onMouseDown={handleDragStart} onTouchStart={handleDragStart}></div>
                                </div>
                            </div>

                            <div className="compare-card">
                                <div className="compare-item current">
                                    <span className="compare-label">Осталось времени</span>
                                    <span className="compare-value" style={{
                                        color: 'green'
                                    }}>{currentTimeRemaining}</span>
                                </div>

                                <div className="compare-arrow">→</div>

                                <div className="compare-item new">
                                    <span className="compare-label">Новый срок</span>
                                    <span className="compare-value new-value">
                                    {addedDevices > 0 ? newTimeRemaining : currentTimeRemaining}
                                </span>
                                </div>
                            </div>

                            <div className="compare-card">
                                <div className="compare-item current">
                                    <span className="compare-label">Стоимость вашего тарифа в месяц</span>
                                    <span className="compare-value" style={{
                                        color: 'white'
                                    }}>{DEVICE_TARIFFS[keyInfo.devices]}₽</span>
                                </div>

                                <div className="compare-arrow">→</div>

                                <div className="compare-item new">
                                    <span className="compare-label">Стоимость нового тарифа в месяц</span>
                                    <span className="compare-value new-value" style={{
                                        color: 'white'
                                    }}>
                                    {DEVICE_TARIFFS[displayDevices]}₽
                                </span>
                                </div>
                            </div>

                            <div className="action-area">
                                <button
                                    className={`btn btn-green ${!canUpgrade ? 'disabled' : ''}`}
                                    onClick={handleChangeTariff}
                                    disabled={!canUpgrade || isChangingTariff}
                                >
                                    {isChangingTariff ? 'Обработка...' : 'Подтвердить смену тарифа'}
                                </button>
                            </div>
                        </div>

                        {/* ==================== ВКЛАДКА УСТРОЙСТВА ==================== */}
                        <div className={`tab-content ${activeTab === 'devices' ? 'active' : ''}`}>
                            <div className="header-section">
                                <h1 className="main-title">Управление устройствами</h1>
                                <p className="subtitle">Список устройств, привязанных к ключу {keyInfo.key_number}</p>
                            </div>

                            {isLoadingDevices ? (
                                <> </>
                            ) : devicesList.length === 0 ? (
                                <div className="empty-devices">
                                    <div className="empty-icon">📱</div>
                                    <p className="empty-title">Нет привязанных устройств</p>
                                    <p className="empty-subtitle">Подключите VPN на устройстве, чтобы оно появилось
                                        здесь</p>
                                </div>
                            ) : (
                                <div className="devices-list">
                                    {devicesList.map((device) => (
                                        <div key={device.hwid} className="device-card">
                                            <div className="device-info">
                                                <div className="device-icon">
                                                    <img
                                                        src={`${ASSET_PATH}/device-${device.platform?.toLowerCase() || 'unknown'}.png`}
                                                        alt={device.platform || 'device'}
                                                        onError={(e) => {
                                                            e.target.src = `${ASSET_PATH}/device-default.svg`;
                                                        }}/>
                                                </div>
                                                <div className="device-details">
                                                    <div className="device-name">
                                                        {device.deviceModel || getPlatformName(device.platform) || 'Неизвестное устройство'}
                                                    </div>
                                                    <div className="device-platform">
                                                        {device.platform && `Платформа: ${getPlatformName(device.platform)}`}
                                                        {device.osVersion && ` • ${device.osVersion}`}
                                                    </div>
                                                    {device.userAgent && (
                                                        <div className="device-user-agent">
                                                            <span>Приложение:</span> {device.userAgent}
                                                        </div>
                                                    )}
                                                    <div className="device-date">
                                                        Подключено: {formatDate(device.createdAt)}
                                                    </div>
                                                    {device.updatedAt !== device.createdAt && (
                                                        <div className="device-date">
                                                            Последняя активность: {formatDate(device.updatedAt)}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                className="device-delete-btn"
                                                onClick={() => handleDeleteDevice(device.hwid)}
                                                disabled={deletingDeviceHwid === device.hwid}
                                                title="Отвязать устройство"
                                            >
                                                {deletingDeviceHwid === device.hwid ? (
                                                    <div className="spinner-small"></div>
                                                ) : (
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                                                         xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2"
                                                              strokeLinecap="round"/>
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Шторка подтверждения */}
                <UpgradeConfirmationSheet
                    isVisible={showUpgradeConfirm}
                    onConfirm={handleUpgradeConfirm}
                    onCancel={handleUpgradeCancel}
                    upgradeDetails={upgradeDetails}
                    keyName={keyInfo.key_number || keyInfo.uuid?.slice(0, 8).toUpperCase()}
                />

                {/* Остальные шторки */}
                <LoadingSheet isVisible={showLoadingSheet} message="Операция выполняется"/>
                <QRCodeSheet isVisible={showQRCode} onClose={handleCloseQR} qrData={keyInfo.url} title="QR-код ключа"/>
                <AppDownloadSheet isVisible={showAppSheet} onClose={handleCloseAppSheet} appData={selectedApp}
                                  selectedPlatform={selectedPlatform}/>
                <AppInstallSheet isVisible={showAppInstallSheet} onClose={handleCloseAppInstallSheet} appData={selectedApp}
                                 selectedPlatform={selectedPlatform}/>
                <ResetTrafficConfirmationSheet isVisible={showResetConfirm} onConfirm={handleResetConfirm}
                                               onCancel={() => setShowResetConfirm(false)} price={RESET_PRICE}
                                               nextResetInfo={timeUntilResetWithOffset}/>
                <ResetConfirmationSheet isVisible={showRegenerateConfirm} onConfirm={handleRegenerateConfirm}
                                        onCancel={() => setShowRegenerateConfirm(false)} price={0} title="Сброс ключа"
                                        warningText="При сбросе ключа вы получите новые данные для подключения. Старый ключ перестанет работать."/>
                <Notification message={notification.message} type={notification.type} isVisible={notification.isVisible}
                              onClose={() => setNotification({...notification, isVisible: false})} duration={3000}/>

                {/* Шторка недостаточного баланса */}
                <InsufficientBalanceSheet
                    isVisible={showInsufficientBalanceSheet}
                    onClose={handleCloseInsufficientBalance}
                    requiredAmount={requiredAmount}
                    currentBalance={currentBalance}
                    onGoWallet={handleGoToWallet}
                />

                {/* Шторка выбора приложения для подключения */}
                <AppSelectionSheet
                    isVisible={showAppSelectionSheet}
                    onClose={() => setShowAppSelectionSheet(false)}
                    onSelectApp={handleAppSelect}
                    apps={apps}
                    selectedPlatform={selectedPlatform}
                />
            </div>
        </>
    );
}
